import { ReactElement, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Styles } from 'common/types/styles';
import { userService } from 'common/services/user.service';
import { theme } from 'common/theme/theme';
import { PDPAttributeHistoryV1Item } from 'common/services/api/content/content-api.types';
import { PocRetailers, RetailerType, WalmartRetailers } from 'common/constants/entities';
import { PdpHistoryItem } from './pdp-history-item';

const styles: Styles = {
  tableContainer: { width: '100%', maxHeight: '500px' },
  descriptionContainer: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 },
  descriptionColumn: { width: 250 },
  noItems: {
    fontSize: 12,
    color: theme.palette.grey[700],
    margin: '100px auto',
    display: 'block',
    width: 'max-content',
  },
  cell: { minWidth: '150px', whiteSpace: 'nowrap' },
  loaderWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: 2,
  },
};

interface Props {
  history: Array<PDPAttributeHistoryV1Item> | null;
  isHistoryLoading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  skipSort?: boolean;
}

export function PdpHistoryContent({
  history,
  isHistoryLoading,
  total,
  page,
  pageSize,
  onPageChange,
  skipSort,
}: Props): ReactElement {
  const isL3 = userService.ensureLevel('L3');
  const isReferenceVariantPID = userService.ensureRetailers([
    ...PocRetailers,
    RetailerType.Target,
    RetailerType.Flywheel,
  ]);

  const hasPagination = total !== undefined && page !== undefined && pageSize !== undefined && onPageChange;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [page]);

  if (isHistoryLoading) {
    return (
      <Box sx={styles.loaderWrapper}>
        <CircularProgress size={14} />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const sortedHistory = skipSort
    ? history
    : history?.sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime());

  return (
    <>
      <TableContainer ref={tableContainerRef} sx={styles.tableContainer}>
        {Boolean(sortedHistory?.length) && (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.cell}>Date</TableCell>
                {userService.ensureRetailers(WalmartRetailers) && <TableCell sx={styles.cell}>Attribute</TableCell>}
                {isReferenceVariantPID && <TableCell sx={styles.cell}>Reference Variant ID</TableCell>}
                <TableCell sx={styles.cell}>Model Name</TableCell>
                {!isL3 && <TableCell sx={styles.cell}>Cost</TableCell>}
                {userService.ensureRetailers(WalmartRetailers) ? (
                  <>
                    <TableCell sx={styles.cell}>Generated Value</TableCell>
                    <TableCell sx={styles.cell}>Revised Value</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={styles.cell}>Process</TableCell>
                    <TableCell sx={styles.cell}>Title</TableCell>
                    <TableCell sx={styles.cell}>Description</TableCell>
                    <TableCell sx={styles.cell}>Features</TableCell>
                    <TableCell sx={styles.cell}>Keywords</TableCell>
                  </>
                )}
                <TableCell sx={styles.cell}>Rationale</TableCell>
                <TableCell sx={styles.cell}>Request</TableCell>
                <TableCell sx={styles.cell}>Response</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHistory?.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <PdpHistoryItem key={i} h={item} />
              ))}
            </TableBody>
          </Table>
        )}
        {!sortedHistory?.length && <Typography sx={styles.noItems}>No history</Typography>}
      </TableContainer>
      {hasPagination && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onPageChange={(_, newPage) => onPageChange(newPage)}
        />
      )}
    </>
  );
}
